import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { FeatureRequest } from '../../services/FeatureRequestService';
import { statusColors } from '../../utils/featureConstants';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CardActions from '@mui/material/CardActions';
import Button from '@mui/material/Button';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import Comment from '@mui/icons-material/Comment';
import { UserData } from '../../services/UserService';

dayjs.extend(relativeTime);

interface FeatureRequestListProps {
  featureRequests: FeatureRequest[];
  commentCounts: {[key: string]: number};
  onVote: (request: FeatureRequest) => void;
  onOpenComments: (request: FeatureRequest) => void;
  user: UserData | null;
}

const FeatureRequestList: React.FC<FeatureRequestListProps> = ({
  featureRequests,
  commentCounts,
  onVote,
  onOpenComments,
  user
}) => {
  const intl = useIntl();

  // Helper function to safely format date
  const formatDate = (date: Date | undefined) => {
    if (!date) return '';
    return dayjs(date).fromNow();
  };

  if (featureRequests.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6">
          <FormattedMessage id="featureRequests.noRequests" />
        </Typography>
        <Typography color="textSecondary">
          <FormattedMessage id="featureRequests.beFirst" />
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={4} padding={4}>
      {featureRequests.map(request => (
        <Grid item xs={12} md={6} key={request.id}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">{request.title}</Typography>
                <Chip 
                  label={intl.formatMessage({ id: `featureRequests.status.${request.status}` })}
                  size="small" 
                  color={statusColors[request.status]} 
                />
              </Box>
              
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 2 }}>
                <FormattedMessage 
                  id="featureRequests.postedBy" 
                  values={{ 
                    name: request.userName || intl.formatMessage({ id: "common.anonymous" }),
                    time: formatDate(request.createdAt)
                  }}
                />
              </Typography>
              
              <Typography variant="body1" paragraph>
                {request.description}
              </Typography>
            </CardContent>
            
            <CardActions>
              <Button
                size="small"
                startIcon={
                  <ArrowUpward 
                    color={request.votedBy.includes(user?.id || '') ? 'primary' : undefined} 
                  />
                }
                onClick={() => onVote(request)}
                disabled={!user}
              >
                {request.votes}
              </Button>
              
              <Button
                size="small"
                startIcon={<Comment />}
                onClick={() => onOpenComments(request)}
              >
                {commentCounts[request.id || ''] || 0}
              </Button>
            </CardActions>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default FeatureRequestList;